import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bot, CheckCircle2, Clock, Filter, Inbox, Loader2, RefreshCw, Sparkles, XCircle, Eye, Send, FileText, Zap, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useAIContentStudio, type IncompletLesson, type ContentDraft, type GenerateResult } from "@/hooks/useAIContentStudio";
import { toast } from "sonner";
import AIAuditTrailPanel from "@/components/admin/AIAuditTrailPanel";

const FIELD_LABELS: Record<string, string> = {
  book_insight_title: "Book Insight Title",
  book_insight_perspective: "Book Insight Perspective",
  book_insight_why_it_matters: "Book Insight — Why It Matters",
  mastery_principle: "Mastery Principle",
  company_case_block: "Company Case",
  applied_case_block: "Applied Case",
  decision_prompt: "Decision Prompt",
  completion_takeaway: "Completion Takeaway",
  current_example_title: "Current Example Title",
  current_example_body: "Current Example Body",
  current_example_why_it_matters: "Current Example — Why It Matters",
  age_variant_11_13: "Age Variant (11–13)",
  age_variant_14_16: "Age Variant (14–16)",
  age_variant_17_plus: "Age Variant (17+)",
  parent_discussion_prompt: "Parent Discussion Prompt",
  parent_reinforcement_note: "Parent Reinforcement Note",
  checkpoint_debrief: "Checkpoint Debrief",
  capstone_debrief: "Capstone Debrief",
  vocab_reinforcement_item: "Vocab Reinforcement",
};

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  pending_review: "bg-amber-500/10 text-amber-500 border-amber-500/20",
  approved: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-500 border-red-500/20",
  published: "bg-primary/10 text-primary border-primary/20",
};

const AIContentStudio = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const { loading, listIncomplete, listDrafts, generateField, regenerateField, approveDraft, rejectDraft, publishDraft } = useAIContentStudio();
  const [activeTab, setActiveTab] = useState("queue");
  const [incompleteLessons, setIncompleteLessons] = useState<IncompletLesson[]>([]);
  const [drafts, setDrafts] = useState<ContentDraft[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedLesson, setSelectedLesson] = useState<IncompletLesson | null>(null);
  const [selectedField, setSelectedField] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [lastResult, setLastResult] = useState<GenerateResult | null>(null);
  const [reviewDraft, setReviewDraft] = useState<ContentDraft | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");

  const loadQueue = useCallback(async () => {
    const data = await listIncomplete();
    setIncompleteLessons(data);
  }, [listIncomplete]);

  const loadDrafts = useCallback(async () => {
    const filters: Record<string, string> = {};
    if (statusFilter !== "all") filters.status = statusFilter;
    const data = await listDrafts(filters);
    setDrafts(data);
  }, [listDrafts, statusFilter]);

  useEffect(() => {
    if (!adminLoading && isAdmin) {
      loadQueue();
      loadDrafts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [adminLoading, isAdmin]);

  useEffect(() => {
    if (activeTab === "drafts" && isAdmin) loadDrafts();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [statusFilter, activeTab]);

  const handleGenerate = async () => {
    if (!selectedLesson || !selectedField) return;
    setGenerating(true);
    const result = await generateField(selectedField, selectedLesson.lesson_id, {
      lesson_name: selectedLesson.lesson_name,
      course_name: selectedLesson.course_name,
    });
    setGenerating(false);
    if (result) {
      setLastResult(result);
      toast.success(`Generated ${FIELD_LABELS[selectedField] || selectedField}`);
      loadDrafts();
    }
  };

  const handleApprove = async () => {
    if (!reviewDraft) return;
    const ok = await approveDraft(reviewDraft.id, reviewNotes);
    if (ok) { setReviewDraft(null); setReviewNotes(""); loadDrafts(); }
  };

  const handleReject = async () => {
    if (!reviewDraft) return;
    const ok = await rejectDraft(reviewDraft.id, reviewNotes);
    if (ok) { setReviewDraft(null); setReviewNotes(""); loadDrafts(); }
  };

  const handlePublish = async (draftId: string) => {
    const ok = await publishDraft(draftId);
    if (ok) loadDrafts();
  };

  if (adminLoading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!isAdmin) { navigate("/dashboard"); return null; }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b border-border/50 bg-background/95 backdrop-blur-md px-4 py-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin-upload")} className="shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-lg flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              AI Content Studio
            </h1>
            <p className="text-xs text-muted-foreground">Generate, review, and publish AI-drafted lesson content</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-muted/30 border border-border/50">
            <TabsTrigger value="queue" className="gap-1.5 text-xs">
              <Inbox className="w-3.5 h-3.5" /> Incomplete Queue
            </TabsTrigger>
            <TabsTrigger value="generate" className="gap-1.5 text-xs">
              <Sparkles className="w-3.5 h-3.5" /> Generate
            </TabsTrigger>
            <TabsTrigger value="drafts" className="gap-1.5 text-xs">
              <FileText className="w-3.5 h-3.5" /> Draft Review
            </TabsTrigger>
            <TabsTrigger value="audit" className="gap-1.5 text-xs">
              <ScrollText className="w-3.5 h-3.5" /> Audit Trail
            </TabsTrigger>
          </TabsList>

          {/* ─── INCOMPLETE QUEUE ─── */}
          <TabsContent value="queue" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{incompleteLessons.length} lessons with missing fields</p>
              <Button variant="outline" size="sm" onClick={loadQueue} className="text-xs gap-1.5">
                <RefreshCw className="w-3 h-3" /> Refresh
              </Button>
            </div>

            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {incompleteLessons.map((lesson) => (
                <div
                  key={lesson.lesson_id}
                  className="rounded-xl border border-border/50 bg-card/60 p-3 hover:bg-card/80 transition-colors cursor-pointer"
                  onClick={() => { setSelectedLesson(lesson); setActiveTab("generate"); setSelectedField(lesson.missing_fields[0] || ""); }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{lesson.lesson_name}</p>
                      <p className="text-xs text-muted-foreground">{lesson.course_name} • Lesson {lesson.lesson_order}</p>
                    </div>
                    <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-500 border-amber-500/20">
                      {lesson.missing_count} missing
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {lesson.missing_fields.map((f) => (
                      <Badge key={f} variant="outline" className="text-[10px] bg-muted/50">
                        {FIELD_LABELS[f] || f}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
              {incompleteLessons.length === 0 && !loading && (
                <div className="text-center py-12 text-muted-foreground text-sm">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                  All lessons are complete
                </div>
              )}
            </div>
          </TabsContent>

          {/* ─── GENERATE ─── */}
          <TabsContent value="generate" className="space-y-4">
            {selectedLesson ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-border/50 bg-card/60 p-4">
                  <p className="text-sm font-semibold text-foreground">{selectedLesson.lesson_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedLesson.course_name} • Lesson {selectedLesson.lesson_order}</p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">Field to Generate</label>
                  <Select value={selectedField} onValueChange={setSelectedField}>
                    <SelectTrigger><SelectValue placeholder="Select field" /></SelectTrigger>
                    <SelectContent>
                      {selectedLesson.missing_fields.map((f) => (
                        <SelectItem key={f} value={f}>{FIELD_LABELS[f] || f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={handleGenerate} disabled={!selectedField || generating} className="gap-2">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  {generating ? "Generating…" : "Generate with AI"}
                </Button>

                {lastResult && (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-primary/20 bg-card/80 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-primary uppercase tracking-wider">Generated Content</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={STATUS_COLORS[lastResult.status]}>
                            {lastResult.status}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">{lastResult.latency_ms}ms • {lastResult.model}</span>
                        </div>
                      </div>
                      <pre className="text-xs text-muted-foreground whitespace-pre-wrap font-mono bg-muted/30 rounded-lg p-3 max-h-60 overflow-y-auto">
                        {JSON.stringify(lastResult.content, null, 2)}
                      </pre>
                    </div>

                    {/* Eval summary */}
                    <div className="rounded-xl border border-border/50 bg-card/60 p-3">
                      <p className="text-xs font-semibold text-foreground mb-2">Quality Evaluation</p>
                      <div className="flex gap-3 text-xs">
                        <span className={lastResult.evaluation.schemaValid ? "text-emerald-500" : "text-red-500"}>
                          Schema: {lastResult.evaluation.schemaValid ? "✓" : "✗"}
                        </span>
                        <span className={lastResult.evaluation.toneValid ? "text-emerald-500" : "text-red-500"}>
                          Tone: {lastResult.evaluation.toneValid ? "✓" : "✗"}
                        </span>
                        <span className={lastResult.evaluation.completenessValid ? "text-emerald-500" : "text-red-500"}>
                          Complete: {lastResult.evaluation.completenessValid ? "✓" : "✗"}
                        </span>
                        <span className="text-muted-foreground">
                          Score: {(lastResult.evaluation.overallScore * 100).toFixed(0)}%
                        </span>
                      </div>
                      {lastResult.evaluation.issues.length > 0 && (
                        <ul className="mt-2 space-y-0.5">
                          {lastResult.evaluation.issues.map((issue, i) => (
                            <li key={i} className="text-[10px] text-amber-500">⚠ {issue}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground text-sm">
                Select a lesson from the Incomplete Queue to start generating
              </div>
            )}
          </TabsContent>

          {/* ─── DRAFT REVIEW ─── */}
          <TabsContent value="drafts" className="space-y-4">
            <div className="flex items-center gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending_review">Pending Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadDrafts} className="text-xs gap-1.5">
                <RefreshCw className="w-3 h-3" /> Refresh
              </Button>
              <span className="text-xs text-muted-foreground">{drafts.length} drafts</span>
            </div>

            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {drafts.map((draft) => (
                <div key={draft.id} className="rounded-xl border border-border/50 bg-card/60 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-foreground">{FIELD_LABELS[draft.field_type] || draft.field_type}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {draft.model_used} • {draft.prompt_version} • {new Date(draft.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={STATUS_COLORS[draft.status]}>
                        {draft.status.replace("_", " ")}
                      </Badge>
                      {draft.status === "pending_review" && (
                        <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => { setReviewDraft(draft); setReviewNotes(""); }}>
                          <Eye className="w-3 h-3" /> Review
                        </Button>
                      )}
                      {draft.status === "approved" && (
                        <Button variant="outline" size="sm" className="text-xs gap-1 border-primary/30 text-primary" onClick={() => handlePublish(draft.id)}>
                          <Send className="w-3 h-3" /> Publish
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Eval badges */}
                  {draft.ai_eval_results && draft.ai_eval_results.length > 0 && (
                    <div className="flex gap-2 mt-2 text-[10px]">
                      {draft.ai_eval_results.map((ev) => (
                        <span key={ev.id} className={`px-1.5 py-0.5 rounded ${ev.overall_score >= 0.7 ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500"}`}>
                          Score: {(ev.overall_score * 100).toFixed(0)}%
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {drafts.length === 0 && !loading && (
                <div className="text-center py-12 text-muted-foreground text-sm">No drafts found</div>
              )}
            </div>
          </TabsContent>

          {/* ─── AUDIT TRAIL ─── */}
          <TabsContent value="audit" className="space-y-4">
            <AIAuditTrailPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Dialog */}
      <Dialog open={!!reviewDraft} onOpenChange={() => setReviewDraft(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" />
              Review: {FIELD_LABELS[reviewDraft?.field_type || ""] || reviewDraft?.field_type}
            </DialogTitle>
          </DialogHeader>

          {reviewDraft && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">
                Model: {reviewDraft.model_used} • Prompt: {reviewDraft.prompt_version} • {new Date(reviewDraft.created_at).toLocaleString()}
              </div>

              <div className="rounded-lg border border-border/50 bg-muted/30 p-4">
                <pre className="text-xs whitespace-pre-wrap font-mono text-foreground">
                  {JSON.stringify(reviewDraft.draft_content, null, 2)}
                </pre>
              </div>

              {reviewDraft.ai_eval_results && reviewDraft.ai_eval_results.length > 0 && (
                <div className="rounded-lg border border-border/50 p-3 space-y-2">
                  <p className="text-xs font-semibold">AI Evaluation</p>
                  {reviewDraft.ai_eval_results.map((ev) => (
                    <div key={ev.id} className="text-xs space-y-1">
                      <div className="flex gap-3">
                        <span className={ev.schema_valid ? "text-emerald-500" : "text-red-500"}>Schema {ev.schema_valid ? "✓" : "✗"}</span>
                        <span className={ev.tone_valid ? "text-emerald-500" : "text-red-500"}>Tone {ev.tone_valid ? "✓" : "✗"}</span>
                        <span className={ev.completeness_valid ? "text-emerald-500" : "text-red-500"}>Complete {ev.completeness_valid ? "✓" : "✗"}</span>
                        <span>Score: {(ev.overall_score * 100).toFixed(0)}%</span>
                      </div>
                      {ev.issues.length > 0 && (
                        <ul>
                          {ev.issues.map((issue, i) => (
                            <li key={i} className="text-amber-500">⚠ {issue}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Textarea
                placeholder="Reviewer notes (optional)…"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                className="text-sm"
              />

              <div className="flex gap-2">
                <Button onClick={handleApprove} className="flex-1 gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </Button>
                <Button onClick={handleReject} variant="destructive" className="flex-1 gap-1.5">
                  <XCircle className="w-4 h-4" /> Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIContentStudio;
